/**
 * Encart Attestations OPERAT — source documentaire du module Travaux d'optimisation.
 *
 * Une attestation = 1 PDF officiel + 1 année + N EFAs rattachées à des sites
 * du même dossier. Le composant liste par année descendante, permet l'upload,
 * la consultation du PDF, et pré-rempli les valeurs Cref/Cabs par site depuis
 * les données OPERAT extraites (P1).
 *
 * Permissions strictes : seuls admin_full + admin + auditeur du dossier écrivent.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FileText, Plus, ExternalLink, Trash2, CheckCircle2, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const currentYear = new Date().getFullYear();
const YEARS_TO_SHOW = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

function AddAttestationDialog({ open, onOpenChange, prospectId, sites, onAdded }) {
    const [year, setYear] = useState(currentYear);
    const [file, setFile] = useState(null);
    const [siteSelections, setSiteSelections] = useState({}); // { site_id: { checked, efa_id, cref, cabs, derniere_conso, annee_reference } }
    const [saving, setSaving] = useState(false);

    const toggleSite = (site_id, checked) => {
        setSiteSelections((prev) => ({
            ...prev,
            [site_id]: { ...(prev[site_id] || {}), checked, efa_id: prev[site_id]?.efa_id || '' },
        }));
    };
    const patchSite = (site_id, patch) => {
        setSiteSelections((prev) => ({ ...prev, [site_id]: { ...(prev[site_id] || {}), ...patch } }));
    };

    const selectedSites = Object.entries(siteSelections).filter(([, v]) => v?.checked);

    const submit = async () => {
        if (!file) return toast.error('Sélectionne un fichier PDF.');
        if (!year) return toast.error('Année requise.');
        if (selectedSites.length === 0) return toast.error('Sélectionne au moins un site concerné.');
        try {
            setSaving(true);
            const efas = selectedSites.map(([site_id, v]) => ({
                site_id,
                efa_id: v.efa_id || null,
                values: {
                    annee_reference: v.annee_reference ? Number(v.annee_reference) : null,
                    cref: v.cref ? Number(v.cref) : null,
                    cabs: v.cabs ? Number(v.cabs) : null,
                    derniere_conso_declaree: v.derniere_conso ? Number(v.derniere_conso) : null,
                    derniere_annee_declaree: year,
                },
            }));
            const fd = new FormData();
            fd.append('year', String(year));
            fd.append('efas', JSON.stringify(efas));
            fd.append('file', file);
            await api.post(`/prospects/${prospectId}/attestations`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Attestation ajoutée');
            onAdded?.();
            onOpenChange(false);
            // reset
            setYear(currentYear); setFile(null); setSiteSelections({});
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur upload attestation');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-attestation-dialog">
                <DialogHeader><DialogTitle>Ajouter une attestation OPERAT</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Année concernée</Label>
                            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="attestation-year" />
                        </div>
                        <div>
                            <Label>Attestation PDF</Label>
                            <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="attestation-file" />
                        </div>
                    </div>

                    <div>
                        <Label className="mb-2 block">Sites concernés par cette attestation</Label>
                        {(sites || []).length === 0 ? (
                            <p className="text-sm text-slate-500 italic">Aucun site sur ce dossier — créer un site principal d&apos;abord.</p>
                        ) : (
                            <div className="space-y-2 border rounded-lg divide-y">
                                {sites.map((s) => {
                                    const sel = siteSelections[s.id] || {};
                                    return (
                                        <div key={s.id} className="p-3">
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    checked={!!sel.checked}
                                                    onCheckedChange={(v) => toggleSite(s.id, !!v)}
                                                    data-testid={`attestation-site-${s.id}`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-semibold text-slate-800 truncate">{s.label}</div>
                                                    <div className="text-xs text-slate-500 truncate">{s.city || '—'} · {s.surface_tertiaire_m2 ? `${s.surface_tertiaire_m2} m²` : 'surface ?'}</div>
                                                </div>
                                            </div>
                                            {sel.checked && (
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3 pl-6">
                                                    <div>
                                                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">EFA</Label>
                                                        <Input placeholder="12345678" value={sel.efa_id || ''} onChange={(e) => patchSite(s.id, { efa_id: e.target.value })} className="h-8" />
                                                    </div>
                                                    <div>
                                                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Année réf.</Label>
                                                        <Input type="number" placeholder="2019" value={sel.annee_reference || ''} onChange={(e) => patchSite(s.id, { annee_reference: e.target.value })} className="h-8" />
                                                    </div>
                                                    <div>
                                                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Cref (kWh)</Label>
                                                        <Input type="number" value={sel.cref || ''} onChange={(e) => patchSite(s.id, { cref: e.target.value })} className="h-8" />
                                                    </div>
                                                    <div>
                                                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Cabs (kWh)</Label>
                                                        <Input type="number" value={sel.cabs || ''} onChange={(e) => patchSite(s.id, { cabs: e.target.value })} className="h-8" />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Dernière conso (kWh)</Label>
                                                        <Input type="number" value={sel.derniere_conso || ''} onChange={(e) => patchSite(s.id, { derniere_conso: e.target.value })} className="h-8" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1">
                            <Sparkles className="w-3 h-3 text-purple-500 mt-0.5" />
                            Ces valeurs seront extraites automatiquement du PDF en Étape 5.5 (Vision Gemini) — vous pourrez confirmer ou corriger avant enregistrement.
                        </p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={submit} disabled={saving} data-testid="attestation-save">
                        {saving ? 'Envoi…' : "Enregistrer l'attestation"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Dialog OCR extraction OPERAT (P1) — Gemini 3.1 Pro Vision.
 * Charge le PDF de l'attestation, appelle l'endpoint d'extraction, affiche les
 * valeurs pré-remplies dans un formulaire éditable pour confirmation humaine
 * OBLIGATOIRE avant l'application aux operat_declarations.
 */
function OperatOcrDialog({ open, onOpenChange, attestation, sites, onApplied }) {
    const [phase, setPhase] = useState('idle'); // 'idle' | 'extracting' | 'review' | 'applying'
    const [extract, setExtract] = useState(null); // { data, confidence, error }
    const [form, setForm] = useState({});
    const [siteId, setSiteId] = useState('');

    // Initial state à l'ouverture
    useMemo(() => {
        if (open && attestation) {
            setPhase('idle');
            setExtract(null);
            setForm({});
            // Pré-sélection : premier site rattaché à cette attestation
            const firstSiteId = attestation.efas?.[0]?.site_id
                || sites?.find((s) => s.is_principal)?.id
                || sites?.[0]?.id
                || '';
            setSiteId(firstSiteId);
        }
    }, [open, attestation, sites]);

    const runExtract = async () => {
        if (!attestation) return;
        setPhase('extracting');
        try {
            const { data } = await api.post(`/operat-attestations/${attestation.id}/extract`);
            setExtract(data);
            if (data?.data) {
                setForm({
                    annee_reference: data.data.annee_reference || '',
                    cref_kwh: data.data.cref_kwh || '',
                    cabs_kwh: data.data.cabs_kwh || '',
                    derniere_annee_declaree: data.data.derniere_annee_declaree || '',
                    derniere_conso_kwh: data.data.derniere_conso_kwh || '',
                    activite_dominante: data.data.activite_dominante || '',
                });
                setPhase('review');
            } else {
                toast.error(data?.error || 'Extraction impossible');
                setPhase('idle');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur extraction');
            setPhase('idle');
        }
    };

    const apply = async () => {
        if (!siteId) { toast.error('Sélectionne un site cible'); return; }
        setPhase('applying');
        try {
            await api.post(`/operat-attestations/${attestation.id}/apply-extract`, {
                site_id: siteId,
                annee_reference: form.annee_reference ? Number(form.annee_reference) : null,
                cref_kwh: form.cref_kwh ? Number(form.cref_kwh) : null,
                cabs_kwh: form.cabs_kwh ? Number(form.cabs_kwh) : null,
                derniere_annee_declaree: form.derniere_annee_declaree ? Number(form.derniere_annee_declaree) : null,
                derniere_conso_kwh: form.derniere_conso_kwh ? Number(form.derniere_conso_kwh) : null,
                activite_dominante: form.activite_dominante || null,
            });
            toast.success('Valeurs OPERAT appliquées et Carte recalculée');
            onApplied?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur application');
            setPhase('review');
        }
    };

    const confidenceStyle = extract?.confidence === 'high'
        ? 'bg-emerald-100 text-emerald-800'
        : extract?.confidence === 'medium'
            ? 'bg-amber-100 text-amber-800'
            : 'bg-rose-100 text-rose-800';

    if (!attestation) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="operat-ocr-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Wand2 className="w-5 h-5 text-purple-600" />
                        Extraction automatique OPERAT
                    </DialogTitle>
                </DialogHeader>

                {phase === 'idle' && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-600">
                            L&apos;IA (Gemini 3.1 Pro Vision) va analyser votre attestation OPERAT et
                            pré-remplir les valeurs Cref, Cabs, année de référence et dernière conso.
                            <b> Une validation humaine reste obligatoire</b> avant l&apos;enregistrement.
                        </p>
                        <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-900">
                            Fichier : <b>{attestation.document?.name || 'attestation.pdf'}</b> · Année {attestation.year}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                            <Button onClick={runExtract} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ocr-start-btn">
                                <Wand2 className="w-4 h-4 mr-1" /> Lancer l&apos;extraction
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {phase === 'extracting' && (
                    <div className="py-6 text-center space-y-2">
                        <div className="w-10 h-10 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin mx-auto" />
                        <p className="text-sm text-slate-600">Analyse en cours…</p>
                        <p className="text-xs text-slate-400">Cela peut prendre 15-30 secondes.</p>
                    </div>
                )}

                {phase === 'review' && (
                    <div className="space-y-3" data-testid="ocr-review-panel">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Confiance IA</span>
                            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${confidenceStyle}`}>
                                {extract?.confidence || 'low'}
                            </span>
                        </div>
                        {extract?.data?.notes && (
                            <div className="text-xs px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-amber-900">
                                Note IA : {extract.data.notes}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs">Année de référence</Label>
                                <Input type="number" value={form.annee_reference || ''}
                                       onChange={(e) => setForm({ ...form, annee_reference: e.target.value })}
                                       data-testid="ocr-form-annee-ref" />
                            </div>
                            <div>
                                <Label className="text-xs">Cref (kWh)</Label>
                                <Input type="number" value={form.cref_kwh || ''}
                                       onChange={(e) => setForm({ ...form, cref_kwh: e.target.value })}
                                       data-testid="ocr-form-cref" />
                            </div>
                            <div>
                                <Label className="text-xs">Cabs (kWh)</Label>
                                <Input type="number" value={form.cabs_kwh || ''}
                                       onChange={(e) => setForm({ ...form, cabs_kwh: e.target.value })}
                                       data-testid="ocr-form-cabs" />
                            </div>
                            <div>
                                <Label className="text-xs">Dernière année déclarée</Label>
                                <Input type="number" value={form.derniere_annee_declaree || ''}
                                       onChange={(e) => setForm({ ...form, derniere_annee_declaree: e.target.value })}
                                       data-testid="ocr-form-last-year" />
                            </div>
                            <div>
                                <Label className="text-xs">Dernière conso (kWh)</Label>
                                <Input type="number" value={form.derniere_conso_kwh || ''}
                                       onChange={(e) => setForm({ ...form, derniere_conso_kwh: e.target.value })}
                                       data-testid="ocr-form-last-conso" />
                            </div>
                            <div>
                                <Label className="text-xs">Activité dominante</Label>
                                <Input value={form.activite_dominante || ''}
                                       onChange={(e) => setForm({ ...form, activite_dominante: e.target.value })}
                                       data-testid="ocr-form-activity" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Site cible (application des valeurs)</Label>
                            <select
                                className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                                value={siteId}
                                onChange={(e) => setSiteId(e.target.value)}
                                data-testid="ocr-form-site"
                            >
                                <option value="">— Choisir —</option>
                                {sites.map((s) => (
                                    <option key={s.id} value={s.id}>{s.label || s.id.slice(0, 8)}{s.is_principal ? ' (principal)' : ''}</option>
                                ))}
                            </select>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                            <Button onClick={apply} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ocr-apply-btn">
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Valider et appliquer
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {phase === 'applying' && (
                    <div className="py-6 text-center space-y-2">
                        <div className="w-8 h-8 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin mx-auto" />
                        <p className="text-sm text-slate-600">Application en cours…</p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

/**
 * Encart Attestations OPERAT.
 * @param {object} bundle - Le bundle retourné par `GET /prospects/:id/energy-card`
 * @param {string} prospectId
 * @param {function} onChanged - callback pour recharger le bundle parent
 */
export default function EnergyOperatAttestationsCard({ bundle, prospectId, onChanged }) {
    const [addOpen, setAddOpen] = useState(false);
    const [ocrTarget, setOcrTarget] = useState(null);

    const { can_write, sites, attestations } = bundle || {};

    // Grouper les attestations par année (avant tout early-return pour respecter les règles hooks)
    const byYear = useMemo(() => {
        const m = {};
        (attestations || []).forEach((a) => {
            const y = a.year;
            (m[y] = m[y] || []).push(a);
        });
        return m;
    }, [attestations]);

    if (!bundle) return null;

    const yearsToShow = Array.from(new Set([...YEARS_TO_SHOW, ...Object.keys(byYear).map(Number)]))
        .sort((a, b) => b - a);

    const handleDelete = async (attId) => {
        if (!window.confirm('Supprimer cette attestation ?')) return;
        try {
            await api.delete(`/operat-attestations/${attId}`);
            toast.success('Attestation supprimée');
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const openPdf = async (attId, name) => {
        try {
            const res = await api.get(`/operat-attestations/${attId}/file`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const w = window.open(url, '_blank', 'noopener,noreferrer');
            if (!w) toast.error("Impossible d'ouvrir le PDF (popup bloquée)");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            void name;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'PDF introuvable');
        }
    };

    return (
        <>
            <Card data-testid="operat-attestations-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-600" />
                        Attestations OPERAT
                        <span className="text-[10px] uppercase tracking-wider font-normal text-slate-400 border border-slate-200 rounded-full px-2 py-0.5 ml-2">Source documentaire</span>
                    </CardTitle>
                    {can_write && (
                        <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="add-attestation-btn">
                            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {yearsToShow.map((year) => {
                            const items = byYear[year] || [];
                            if (items.length === 0) {
                                return (
                                    <div key={year} className="flex items-center justify-between py-2 px-3 rounded-lg border border-dashed border-slate-200 text-sm text-slate-500" data-testid={`attestation-empty-${year}`}>
                                        <span><b className="text-slate-700 mr-2">{year}</b> Aucune attestation renseignée</span>
                                        {can_write && (
                                            <Button size="sm" variant="ghost" onClick={() => setAddOpen(true)}>
                                                <Plus className="w-3 h-3 mr-1" /> Ajouter
                                            </Button>
                                        )}
                                    </div>
                                );
                            }
                            return (
                                <div key={year} className="space-y-1">
                                    {items.map((att) => {
                                        const nbSites = (att.efas || []).length;
                                        return (
                                            <div key={att.id} className="flex items-center justify-between p-3 rounded-lg bg-purple-50/40 border border-purple-100" data-testid={`attestation-${att.id}`}>
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-bold text-slate-800">{year}</span>
                                                            <span className="text-sm text-slate-700 truncate">{att.document?.name || 'Attestation OPERAT'}</span>
                                                            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-1.5 py-0.5">Document disponible</span>
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-0.5">
                                                            {nbSites > 0 ? `${nbSites} site${nbSites > 1 ? 's' : ''} concerné${nbSites > 1 ? 's' : ''}` : 'Aucun site rattaché'}
                                                            {att.efas?.[0]?.efa_id && ` · EFA ${att.efas[0].efa_id}`}
                                                            {nbSites > 1 && ' · multi-sites'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Button size="sm" variant="ghost" onClick={() => openPdf(att.id, att.document?.name)} data-testid={`attestation-view-${att.id}`}>
                                                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Voir
                                                    </Button>
                                                    {can_write && (
                                                        <Button size="sm" variant="ghost" onClick={() => setOcrTarget(att)} className="text-purple-600 hover:text-purple-700" data-testid={`attestation-ocr-${att.id}`}>
                                                            <Wand2 className="w-3.5 h-3.5 mr-1" /> Extraire
                                                        </Button>
                                                    )}
                                                    {can_write && (
                                                        <Button size="sm" variant="ghost" onClick={() => handleDelete(att.id)} className="text-slate-400 hover:text-rose-600" data-testid={`attestation-delete-${att.id}`}>
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-3 italic">
                        Les valeurs des attestations (Cref, Cabs, année de référence…) sont prioritaires sur les saisies manuelles dans la Carte d&apos;identité énergétique.
                    </p>
                </CardContent>
            </Card>

            <AddAttestationDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                prospectId={prospectId}
                sites={sites || []}
                onAdded={onChanged}
            />
            <OperatOcrDialog
                open={!!ocrTarget}
                onOpenChange={(v) => !v && setOcrTarget(null)}
                attestation={ocrTarget}
                sites={sites || []}
                onApplied={() => { setOcrTarget(null); onChanged?.(); }}
            />
        </>
    );
}
